import { getFileExtension } from "@/shared/utils/fileHelpers";

const BLOCKED_EXTENSIONS = new Set([
  "exe", "sh", "bat", "cmd", "ps1", "js", "mjs", "cjs",
  "vbs", "wsf", "com", "scr", "pif", "msi", "dll", "sys",
  "jar", "py", "rb", "pl", "php", "asp", "aspx", "jsp",
]);

const ALLOWED_MIME_TYPES = new Set([
  // PDF
  "application/pdf",
  // Images
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/svg+xml",
  // MS Word
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  // MS Excel
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  // MS PowerPoint
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // Video
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/x-m4v",
]);

export const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
export const MAX_VIDEO_FILE_SIZE = 500 * 1024 * 1024; // 500 MB

export function validateFile(file: File): { valid: boolean; error?: string } {
  // 1. Check blocked extensions
  const ext = getFileExtension(file.name);
  if (BLOCKED_EXTENSIONS.has(ext)) {
    return { valid: false, error: `File type .${ext} is not allowed` };
  }

  // 2. Check MIME type allowlist
  if (!ALLOWED_MIME_TYPES.has(file.type)) {
    return { valid: false, error: "This file type is not supported. Allowed: PDF, images, videos, MS Office documents" };
  }

  // 3. Check file size (videos get a larger cap)
  const isVideo = file.type.startsWith("video/");
  if (isVideo && file.size > MAX_VIDEO_FILE_SIZE) {
    return { valid: false, error: "Video exceeds 500MB limit" };
  }
  if (!isVideo && file.size > MAX_FILE_SIZE) {
    return { valid: false, error: "File exceeds 25MB limit" };
  }

  // 4. Double-check: extension matches MIME category
  const mimeCategory = file.type.split("/")[0];
  const extIsImage = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext);
  if (mimeCategory === "image" && !extIsImage) {
    return { valid: false, error: "File extension does not match its content type" };
  }

  return { valid: true };
}
